import { HTTP_STATUS } from '@federa/const/http-status';
import { ACTIVE_BRAND } from './brand-registry.ts';
import type { LicenseType, ReferralStatus } from '@federa/types/enums-deal-lifecycle';

export const VALID_LICENSE_TYPES = [
	'Agent',
	'Broker',
	'Associate Broker',
	'Broker in Charge',
	'Principal Broker',
	'Designated Broker',
	'Brokerage',
	'Mortgage Broker',
	'Mortgage Officer',
] as const satisfies readonly LicenseType[];

export const VALID_REFERRAL_STATUS = [
	'In Review',
	'Qualified',
	'Not Qualified',
	'Available',
	'Unavailable',
] as const satisfies readonly ReferralStatus[];

export const DEFAULT_LOCALE = 'en-US' as const;

/** Open Graph locale (BCP-47 with underscore separator): the OG protocol requires `en_US`, while DEFAULT_LOCALE uses the standard hyphen form (`en-US`). */
export const DEFAULT_OG_LOCALE = 'en_US' as const;

/** Default currency code (ISO 4217 uppercase), for anything that stores or displays a currency.
 *
 *  The Stripe API wants the LOWERCASE form, which is why {@link DEFAULT_CURRENCY} exists beside this and is NOT a
 *  respelling to fold away. The live evidence is the four consumers of the lowercase const:
 *  `utils/payments/stripe-body-builders.ts`,
 *  `utils/payments/pay-factory-request-fields.ts`, `flows/banc-account-bank-instructions.ts` and
 *  `flows/checkout-create.ts`, each handing it straight to Stripe. */
export const DEFAULT_CURRENCY_ISO = 'USD' as const;

export const DEFAULT_CURRENCY = 'usd' as const;

/** The sign set BEFORE an amount in {@link DEFAULT_CURRENCY_ISO}, for a figure drawn as sign + number + unit on one
 *  line (the Noor rate figure, the currency input's prefix). `formatUsd` composes the same sign INSIDE a formatted
 *  string; this is for the sites that set the sign apart from the number. */
export const DEFAULT_CURRENCY_SIGN = '$' as const;

/** Default ISO 3166-1 alpha-2 country code, aligned with the single-locale `en-US` rule. */
export const DEFAULT_COUNTRY_CODE = 'US' as const;

export const DEFAULT_CONVERSATION_TYPE = 'alex' as const;

/** localStorage key holding `{ userId, room }` -- the viewer's assistant conversation, so the panel's socket dials
 *  before the conversations read answers (`utils/assistant/assistant-room-cache.ts`). */
export const ASSISTANT_ROOM_CACHE_STORAGE_KEY = 'federa_assistant_room';

/**
 * Keep-alive identity of the MOBILE assistant's React root (`useMFEContainerMount`), shared by the two containers that
 * take turns holding it: the `/assistant` route, and the app shell's parked pre-warm container for a returning
 * assistant user (`useAssistantRouteWarm`). One key, so the route re-attaches the root the shell already built.
 */
export const ASSISTANT_ROUTE_KEEP_ALIVE_KEY = 'assistant-route';

/**
 * How recently an assistant room must have been created for the pane to treat its history as EMPTY BY CONSTRUCTION
 * and skip the history read on open (`isFreshAssistantRoom`, `spa/chats/frontend/src/assistant/assistant-room-freshness.ts`).
 * A room minted by this very open has no messages; the socket's session-init backfill still delivers anything that
 * lands in the window.
 */
export const ASSISTANT_ROOM_FRESH_WINDOW_MS = 60_000;
export const DEFAULT_FRIENDLY_NAME = 'Chat Room';
/** Maximum length for conversation friendly names */
export const MAX_FRIENDLY_NAME_LENGTH = 255;
/** The `conversations.conversation_type` vocabulary -- every token the room-create validator admits, in ONE record.
 *  EVERY member belongs here: a vocabulary assembled from two homes is a token that can be added in one place and
 *  missed in the other, which is exactly how `KAIZENS` came to be spelled outside this record. */
export const CONVERSATION_TYPES = {
	WISHLIST: 'wishlist',
	AGENT: 'agent',
	TRANSACTION: 'transaction',
	LICENCE: 'licence',
	ISSUES: 'issues',
	KAIZENS: 'kaizens',
	SUPPORT: 'support',
} as const;
export const ALEX_ADMIN_IDENTITY = 'alex_admin' as const;

/** conversations.attributes key stashing a synthetic prior assistant turn for the SDK first-turn bridge. Writer: the invite stash nodes; reader: agent-run-steps.ts. */
export const PENDING_FIRST_TURN_CONTEXT_KEY = 'pending_first_turn_context' as const;

/** Initial greeting Alex sends when a conversation is created during user provisioning or invite flows -- SSOT imported by the provisioning helpers and both flows. */
export const ALEX_INITIAL_GREETING = `Hello, my name is Alex with ${ACTIVE_BRAND.name}. I'm here to assist you with anything you need.`;

/** Admin/system prompt prefix used when an invite carries a lead-intent context blob. The AI must NOT reveal it is a system instruction. */
export const INVITE_AI_GREETING_PROMPT = `You are starting a fresh conversation with a new user. Send a warm, natural first message as if this is the very first thing in the chat, introducing yourself as Alex from ${ACTIVE_BRAND.name}. Use the context below to make the greeting personally relevant and offer concrete next steps where helpful. Do not mention that this is a system instruction, do not say words like 'lead' or 'admin', and do not echo the context verbatim -- just speak directly to the user.\n\nContext: \n`;

export const ASSISTANT_CONFIG = {
	alex: {
		id: 'asst_OOr8w7qFFikY6Temhn8UZ6B6',
		identity: 'alex',
	},
	[ALEX_ADMIN_IDENTITY]: {
		id: 'asst_hyW1UwuRPS2eWaLvG9s64sqX',
		identity: ALEX_ADMIN_IDENTITY,
	},
} as const satisfies Record<string, { id: string; identity: string }>;

export const PRODIGY_SUMMARY_AGENT = 'prodigy_summary' as const;

export const ISSUE_PARSER_AGENT = 'issue_parser' as const;

/** Standard response for agent SDK function triggers */
export const AGENT_TRIGGERED_RESPONSE = {
	message: 'Agents SDK AI function triggered',
	status: HTTP_STATUS.OK,
	success: true,
} as const;
